import { Theme } from '@mui/material';
import { IStyles } from '../../../../../../resource';
import { MONTHLY_COMMENTARY_IMAGE_WIDTH_WEB } from '../monthlyCommentaryImage';

// Styles matching the old PDFViewer component
const styles: IStyles = {
  paper: (theme: Theme) => ({
    p: theme.spacing(24),
    border: '1px solid #e5e7eb',
    borderRadius: 2,
    fontFamily: 'Calibri',
    '& *': {
      fontFamily: 'Calibri',
    },
  }),
  intro: (theme: Theme) => ({
    p: theme.spacing(0, 20),
    backgroundColor: '#d2d2d4',
    // Tighter gap between commentary block and INVESTMENTPHILOSOPHIE / chart row below
    m: theme.spacing(40, 0, 20),
    cursor: 'pointer',
    '&:hover': {
      background: '#c3c3c3',
    },
  }),
  subIntro: () => ({
    cursor: 'pointer',
    '&:hover': {
      background: '#c3c3c3',
    },
  }),
  /** Monatskommentar portrait — fixed width for all SHV factsheets. */
  introImage: (theme: Theme, imageWidthPx?: number) => ({
    width: imageWidthPx ?? MONTHLY_COMMENTARY_IMAGE_WIDTH_WEB,
    maxWidth: '22%',
    flexShrink: 0,
    alignSelf: 'flex-start',
    img: {
      background: 'transparent !important',
      width: '100%',
      height: 'auto',
      maxHeight: 220,
      objectFit: 'contain',
      display: 'block',
    },
  }),
  introRow: () => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    pb: 1,
  }),
  introTextColumns: () => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  }),
  widget: (theme: Theme) => ({
    // mb: theme.spacing(40),
  }),
  divider: () => ({
    borderColor: '#a7a8ab',
  }),
  blackDivider: () => ({
    borderColor: '#000000',
  }),
  contact: () => ({
    cursor: 'pointer',
    width: 250,
    padding: 20,
    '&:hover': {
      background: '#c3c3c3',
    },
  }),
  socialSites: () => ({
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 20,
    '&:hover': {
      background: '#c3c3c3',
    },
  }),
  chartsLabel: (theme: Theme, templateColor?: string) => ({
    mb: theme.spacing(30),
    ml: theme.spacing(50),
    cursor: 'pointer',
    span: {
      color: templateColor || '#003f7a',
      marginRight: '8px',
      fontWeight: 'bold',
    },
    '&:hover': {
      background: '#c3c3c3',
    },
    color: '#000',
    fontSize: 14,
  }),
  /** Line-chart legend under “Wertentwicklung seit Auflage” (dash + fund name). */
  historyChartLegend: (theme: Theme, templateColor?: string) => ({
    display: 'flex',
    alignItems: 'center',
    mt: 0.5,
    ml: theme.spacing(2),
    cursor: 'pointer',
    color: '#000',
    fontSize: 14,
    '& .history-chart-legend-line': {
      display: 'inline-block',
      width: 22,
      height: 3.5,
      borderRadius: 1,
      backgroundColor: templateColor || '#003f7a',
      marginRight: '8px',
      flexShrink: 0,
    },
    '&:hover': {
      background: '#c3c3c3',
    },
  }),
  businessTable: () => ({
    marginTop: '-10px',
  }),
  site: (theme: Theme) => ({
    border: `1px solid transparent`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      borderColor: theme.palette.secondary.main,
    },
  }),
  // Header styles
  headerContainer: () => ({
    pt: 2.5,
    pb: 2.5,
    display: 'flex',
    justifyContent: 'space-between',
  }),
  logoSectionContainer: () => ({
    width: 55,
    position: 'relative',
    mt: 1.25,
  }),
  logoBoxTop: (theme: Theme, templateColor?: string) => ({
    background: templateColor || '#003f7a',
    width: 55,
    height: 20,
  }),
  logoBoxBottom: (theme: Theme, templateColor?: string) => ({
    background: templateColor || '#003f7a',
    width: 55,
    height: 60,
    marginTop: '3px',
    position: 'relative',
  }),
  titleSectionContainer: () => ({
    width: 350,
    mt: 1.5,
  }),
  titleSectionHeader: () => ({
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
  }),
  factsFiguresText: () => ({
    color: '#6d6e70',
    fontSize: 13,
    fontWeight: 'bold',
    mr: 0.625,
  }),
  dateText: () => ({
    color: '#949599',
    fontSize: 13,
    fontWeight: 'bold',
  }),
  mainTitle: (theme: Theme, templateColor?: string) => ({
    color: templateColor || '#003f7a',
    fontWeight: 'bold',
    fontSize: 30,
    lineHeight: 1,
    fontFamily: 'Calibri',
    mt: 2,
  }),
  titleAddition: () => ({
    color: '#6d6e70',
    fontWeight: 'normal',
  }),
  contactSection: () => ({
    mt: 1.25,
    textAlign: 'right',
    fontSize: 10.5,
    color: '#949599',
    lineHeight: 1.25,
  }),
  contactBoldText: () => ({
    fontWeight: 'bold',
  }),
  rightLogoContainer: () => ({
    width: 100,
    height: 100,
    mt: 0.875,
    ml: '16px !important',
  }),
  // Disclaimer styles
  disclaimerText: (theme: Theme, size?: number) => ({
    color: '#949599',
    fontSize: size || 16,
    fontWeight: size === 24 ? 600 : 'bold',
    mb: 20,
  }),
  // Intro text — browser German hyphenation; pre-line keeps Enter as a real line break
  introText: () => ({
    textAlign: 'justify',
    color: '#000000',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.25,
    whiteSpace: 'pre-line',
    hyphens: 'auto',
    WebkitHyphens: 'auto',
    msHyphens: 'auto',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
  }),
  contentTitle: () => ({
    fontFamily: 'Changa',
    color: '#6d6e70',
    fontWeight: 400,
    fontSize: '1.6rem !important',
    lineHeight: 1,
    mb: 20,
    pb: 4,
    borderBottom: '2px solid #6d6e70',
  }),
  // Content text styles
  contentText: () => ({
    color: '#000',
    fontSize: 14,
    textAlign: 'justify',
    mb: 10,
  }),
  contentTitle2: (template: any) => ({
    fontFamily: 'Changa',
    color: template.color,
    fontSize: '1.6rem !important',
    fontWeight: 400,
    lineHeight: 1,
    mb: 12,
    pb: 3,
    borderBottom: '2px solid #6d6e70',
  }),
  // Performance periods styles
  performancePeriodsHeaderRow: () => ({
    direction: 'row',
    width: '100%',
    mt: 32,
    borderBottom: '1px solid #a7a8ab',
  }),
  performancePeriodsHeaderCell: (theme: Theme, isFirst?: boolean) => ({
    flex: 1,
    textAlign: isFirst ? 'left' : 'center',
  }),
  performancePeriodsHeaderCellText: () => ({
    color: '#000',
    fontWeight: 'bold !important',
  }),
  performancePeriodsValuesContainer: (theme: Theme, isColumn?: boolean) => ({
    direction: isColumn ? 'column' : 'row',
    width: '100%',
    mt: 1,
    mx: -1,
  }),
  performancePeriodsValueRow: () => ({
    direction: 'row',
    width: '100%',
    mt: 1,
    mx: -1,
  }),
  performancePeriodsValueCell: () => ({
    flex: 1,
    margin: '2px',
    px: '8px',
    background: '#D2D2D4',
    textAlign: 'left',
    wordBreak: 'break-word',
  }),
  performancePeriodsValueText: () => ({
    color: '#000',
  }),
  // Risk indicators styles
  riskIndicatorsHeaderRow: () => ({
    direction: 'row',
    width: '100%',
    mt: 32,
    borderBottom: '1px solid #a7a8ab',
  }),
  riskIndicatorsHeaderCell: (theme: Theme, isFirst?: boolean) => ({
    flex: 1,
    textAlign: isFirst ? 'left' : 'center',
  }),
  riskIndicatorsHeaderCellText: () => ({
    fontWeight: 'bold !important',
  }),
  riskIndicatorsValueRow: () => ({
    direction: 'row',
    width: '100%',
    mt: 1,
    mx: -1,
  }),
  riskIndicatorsValueCell: () => ({
    flex: 1,
    margin: '2px',
    px: '8px',
    background: '#D2D2D4',
    textAlign: 'left',
    wordBreak: 'break-word',
  }),
  riskIndicatorsValueText: () => ({
    color: '#000',
  }),
  // Footer notes
  footerNotes: () => ({
    mt: 40,
    lineHeight: 1.5,
    color: '#000',
    fontSize: '0.75rem',
    textAlign: 'right',
  }),
  // Disclaimer full text
  disclaimerFullText: (theme: Theme, templateColor?: string) => ({
    mt: 'auto',
    fontSize: '10px !important',
    textAlign: 'justify',
    lineHeight: 1.1,
    '& span': {
      color: templateColor || '#003f7a',
    },
  }),
  disclaimerFullTextSmall: () => ({
    color: '#000',
    mt: 10,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    textAlign: 'justify',
    '& span': {
      color: '#003f7a',
    },
  }),
  // Contact page styles
  contactHeaderDivider: () => ({
    mt: 40,
    mb: 5,
    borderColor: 'black',
  }),
  contactHeaderTitle: () => ({
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.2,
    color: '#946A4E',
    textAlign: 'center',
  }),
  contactIntroText: () => ({
    mt: 16,
    textAlign: 'center',
    fontSize: '1rem',
    lineHeight: 1.5,
  }),
  contactName: () => ({
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.2,
    color: '#946A4E',
  }),
  contactRole: () => ({
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: 'bold',
  }),
  contactEmailTel: () => ({
    fontSize: '1rem',
    lineHeight: 1.5,
  }),
  socialSitesHeader: () => ({
    mt: 30,
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.2,
    color: '#8F8987',
    textAlign: 'center',
  }),
  disclaimerDivider: () => ({
    mb: 10,
  }),
  // Grid container styles
  introGridContainer: () => ({
    pb: 8,
  }),
  // Business table styles (single-column compact layout)
  businessTableContainer: () => ({
    mt: '8px',
  }),
  businessTableHeaderCell: () => ({
    py: '3px',
    px: '4px',
    background: '#D2D2D4',
    '& .MuiTypography-root': {
      color: '#484848',
      fontWeight: 600,
      fontSize: '9px',
      lineHeight: 1.2,
    },
    borderBottom: '1px solid #949599',
  }),
  businessTableBodyCell: () => ({
    py: '2px',
    px: '4px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    '& .MuiTypography-root': {
      color: '#484848',
      fontWeight: 500,
      fontSize: '8px',
      lineHeight: 1.2,
    },
    '& img': {
      width: '16px',
      height: '16px',
      objectFit: 'contain' as const,
    },
  }),
};

// Plain CSS styles for inline style prop (not part of IStyles)
export const inlineStyles = {
  logoImage: {
    position: 'absolute' as const,
    top: '8px',
    left: '8px',
    width: '30px',
    height: '30px',
    objectFit: 'contain' as const,
  },
  rightLogoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
  },
  iconImage: {
    width: '40px',
    marginRight: '10px',
  },
  logoGridImage: {
    width: 200,
  },
  socialSiteImage: {
    width: 72,
  },
  contactImage: {
    width: '100%',
  },
};

// Export styles
export default styles;
